import React, { useState, useRef } from 'react';
import imageCompression from 'browser-image-compression';
import { api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Camera, X, Loader2, ZoomIn, Image } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';

// Target: ~300 KB, hard cap 500 KB. Re-comprime iterativamente se il primo pass
// non scende sotto la soglia (foto molto dettagliate).
const COMPRESSION_TARGET_KB = 300;
const COMPRESSION_HARD_CAP_KB = 500;

async function compressForUpload(file) {
  // Skip GIF (animati) — la compressione canvas li trasformerebbe in singolo frame
  if (file.type === 'image/gif') return file;

  const baseOptions = {
    maxSizeMB: COMPRESSION_TARGET_KB / 1024, // 0.293 MB target
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    initialQuality: 0.82,
    fileType: file.type === 'image/png' ? 'image/jpeg' : file.type, // PNG → JPEG (più piccolo)
  };

  let compressed = await imageCompression(file, baseOptions);

  // Se ancora oltre l'hard cap, secondo pass più aggressivo
  if (compressed.size > COMPRESSION_HARD_CAP_KB * 1024) {
    compressed = await imageCompression(compressed, {
      ...baseOptions,
      maxSizeMB: (COMPRESSION_HARD_CAP_KB - 50) / 1024,
      maxWidthOrHeight: 1600,
      initialQuality: 0.7,
    });
  }

  // Mantieni il filename originale ma forza estensione .jpg se abbiamo convertito PNG
  const finalName = (file.type === 'image/png' && compressed.type === 'image/jpeg')
    ? file.name.replace(/\.png$/i, '.jpg')
    : file.name;
  return new File([compressed], finalName, { type: compressed.type, lastModified: Date.now() });
}

const PhotoUpload = ({ ticketId, photos = [], onPhotoAdded }) => {
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo file non supportato. Usa JPEG, PNG, WebP o GIF');
      return;
    }

    // Hard limit prima compressione (max 20MB raw — sopra è patologico)
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 20MB');
      return;
    }

    try {
      let toUpload = file;
      const originalKB = Math.round(file.size / 1024);

      // Compressione client-side (skippa GIF/animate)
      if (file.type !== 'image/gif') {
        setCompressing(true);
        try {
          toUpload = await compressForUpload(file);
          const newKB = Math.round(toUpload.size / 1024);
          if (newKB < originalKB) {
            toast.info(`Foto compressa: ${originalKB} KB → ${newKB} KB`);
          }
        } catch (err) {
          console.warn('Compression failed, uploading original:', err);
        } finally {
          setCompressing(false);
        }
      }

      setUploading(true);
      const formData = new FormData();
      formData.append('file', toUpload);

      const response = await api.post(`/tickets/${ticketId}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Foto caricata con successo');
      if (onPhotoAdded) {
        onPhotoAdded(response.data.url);
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Errore nel caricamento della foto');
    } finally {
      setUploading(false);
      setCompressing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const busy = uploading || compressing;
  const btnLabel = compressing
    ? 'Ottimizzazione...'
    : (uploading ? 'Caricamento...' : 'Aggiungi Foto');

  return (
    <div className="space-y-4" data-testid="photo-upload">
      {/* Upload button */}
      <div className="flex items-center gap-4">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          data-testid="photo-input"
        />
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="gap-2"
          data-testid="upload-photo-button"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Camera className="w-4 h-4" />
          )}
          {btnLabel}
        </Button>
        <span className="text-xs text-slate-500">JPEG, PNG, WebP, GIF · ottimizzata automaticamente a ~300 KB</span>
      </div>

      {/* Photo gallery */}
      {photos && photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((photo, index) => (
            <Dialog key={photo || `photo-${index}`}>
              <DialogTrigger asChild>
                <div 
                  className="relative group cursor-pointer aspect-square rounded-lg overflow-hidden border border-slate-200 hover:border-blue-500 transition-colors"
                  data-testid={`photo-${index}`}
                >
                  <img
                    src={photo}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">Errore</text></svg>';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-3xl p-0 overflow-hidden">
                <img
                  src={photo}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
              </DialogContent>
            </Dialog>
          ))}
        </div>
      )}

      {/* Empty state */}
      {(!photos || photos.length === 0) && (
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
          <Image className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Nessuna foto allegata</p>
          <Button
            variant="link"
            className="mt-2 text-blue-600"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Carica la prima foto
          </Button>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
